/**
 * SEO utilities for visual components
 * Generates structured data for maximum search visibility
 */

interface StructuredDataParams {
  type: 'ImageObject' | 'Diagram' | 'Chart' | 'Infographic';
  name: string;
  description: string;
  keywords?: string[];
  author?: string;
  datePublished?: string;
  dateModified?: string;
  contentUrl: string;
  encodingFormat: string;
  width: string;
  height: string;
}

export function generateStructuredData(params: StructuredDataParams) {
  const {
    type,
    name,
    description,
    keywords,
    author = 'LeadPops',
    datePublished,
    dateModified,
    contentUrl,
    encodingFormat,
    width,
    height
  } = params;
  
  // Base ImageObject schema
  const structuredData: any = {
    '@context': 'https://schema.org',
    '@type': type,
    name,
    description,
    contentUrl,
    encodingFormat,
    width: {
      '@type': 'QuantitativeValue',
      value: width,
      unitCode: 'E37' // pixels
    },
    height: {
      '@type': 'QuantitativeValue',
      value: height,
      unitCode: 'E37' // pixels
    },
    creator: {
      '@type': 'Organization',
      name: author,
      url: 'https://leadpops.com',
      logo: 'https://leadpops.com/images/logo.png'
    },
    copyrightHolder: {
      '@type': 'Organization',
      name: 'LeadPops Inc.'
    },
    copyrightYear: new Date().getFullYear(),
    license: 'https://leadpops.com/terms',
    acquireLicensePage: 'https://leadpops.com/contact'
  };
  
  // Add optional fields
  if (datePublished) {
    structuredData.datePublished = datePublished;
  }
  
  if (dateModified) {
    structuredData.dateModified = dateModified;
  }
  
  if (keywords && keywords.length > 0) {
    structuredData.keywords = keywords.join(', ');
  }
  
  // Add education/business context for better SEO
  structuredData.educationalUse = 'Professional Development';
  structuredData.isAccessibleForFree = true;
  structuredData.isFamilyFriendly = true;
  structuredData.inLanguage = 'en-US';
  
  // Add related entities for topic relevance
  structuredData.about = {
    '@type': 'Thing',
    name: 'Mortgage Lead Generation',
    description: 'Professional strategies and tools for generating mortgage leads'
  };
  
  structuredData.mentions = [
    {
      '@type': 'Thing',
      name: 'Mortgage Marketing'
    },
    {
      '@type': 'Thing',
      name: 'Lead Generation'
    },
    {
      '@type': 'Organization',
      name: 'LeadPops'
    }
  ];
  
  return structuredData;
}

export function generateVisualMetaTags(params: {
  title: string;
  description: string;
  url: string;
  imageUrl: string;
  width: number;
  height: number;
  keywords?: string[];
}) {
  const { title, description, url, imageUrl, width, height, keywords } = params;
  
  return {
    // Basic Meta Tags
    title,
    description,
    keywords: keywords?.join(', '),
    
    // Open Graph
    'og:title': title,
    'og:description': description,
    'og:url': url,
    'og:image': imageUrl,
    'og:image:width': width.toString(),
    'og:image:height': height.toString(),
    'og:image:alt': title,
    'og:type': 'website',
    'og:site_name': 'LeadPops',
    
    // Twitter Card
    'twitter:card': 'summary_large_image',
    'twitter:title': title,
    'twitter:description': description,
    'twitter:image': imageUrl,
    'twitter:image:alt': title,
    'twitter:site': '@leadpops',
    'twitter:creator': '@leadpops',
    
    // Additional SEO
    'article:author': 'LeadPops',
    'article:publisher': 'https://leadpops.com',
    robots: 'index, follow, max-image-preview:large',
    googlebot: 'index, follow, max-image-preview:large, max-snippet:-1'
  };
}

export function generateBreadcrumbSchema(params: {
  items: Array<{ name: string; url: string }>;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: params.items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };
}

export function generateHowToSchema(params: {
  name: string;
  description: string;
  steps: Array<{ name: string; text: string; image?: string }>;
  totalTime?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: params.name,
    description: params.description,
    totalTime: params.totalTime,
    supply: [],
    tool: [],
    step: params.steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
      image: step.image
    }))
  };
}

export function generateFAQSchema(params: {
  questions: Array<{ question: string; answer: string }>;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: params.questions.map(qa => ({
      '@type': 'Question',
      name: qa.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: qa.answer
      }
    }))
  };
}